import { compactRunMachineState } from "./combatPerformance";
import { normalizeRefillPreview } from "./combatState";
import { breacher } from "./generated/protocol.js";
import type { RunState, RunView } from "./types";

export type WasmStatus = "loading" | "ready" | "unavailable" | "version-mismatch";

export interface SimWasmStatus {
  status: Exclude<WasmStatus, "loading">;
  reason?: string;
}

export interface AutomaticSlide {
  axis: "row" | "col";
  index: number;
  distance: number;
  nextCursor: number;
}

export type PredictionAction =
  | { kind: "slide"; axis: "row" | "col"; index: number; distance: number; automated: boolean }
  | { kind: "lock_row"; row: number }
  | { kind: "skill"; skill: string }
  | { kind: "chest"; open: boolean }
  | { kind: "retreat" };

export interface PredictedRun {
  view: RunView;
  stateHash: string;
  revision: number;
  operationSeq: number;
  needsAuthorityEvent: boolean;
}

declare global {
  interface Window {
    breacherSim?: {
      version?: string;
      dispatch?: (request: Uint8Array) => Uint8Array;
      preview?: (request: Uint8Array) => Uint8Array;
      automate?: (request: Uint8Array) => Uint8Array;
      replay?: (request: Uint8Array) => Uint8Array;
    };
    Go?: new () => {
      importObject: WebAssembly.Imports;
      run(instance: WebAssembly.Instance): Promise<void>;
    };
  }
}

// First 8 bytes of SHA-256(sim.CurrentEngineVersion), checked by make wasm-check.
const EXPECTED_VERSION = "08975dd423b79c20";
const MACHINE_INIT = 1;
const MACHINE_APPLY = 2;
const MACHINE_ADVANCE = 3;
const MACHINE_CONFIRM = 4;
const MACHINE_ROLLBACK = 5;
const MACHINE_RELEASE = 6;
let loadPromise: Promise<SimWasmStatus> | null = null;
let machineView: RunView | null = null;

export function loadSimWasm(): Promise<SimWasmStatus> {
  if (!loadPromise) loadPromise = load();
  return loadPromise;
}

export function initializeRunMachine(view: RunView, command = MACHINE_INIT): boolean {
  const dispatch = window.breacherSim?.dispatch;
  if (typeof dispatch !== "function" || !view.randomWindow || !view.machineConfig) return false;
  try {
    const result = decodeMachineResult(
      dispatch(
        breacher.v2.RunMachineRequest.encode({ command, snapshot: encodeSnapshot(view) }).finish(),
      ),
    );
    if (result.error) return false;
    machineView = decodeSnapshot(
      result.snapshot,
      view.serverNow,
      view.refillPreview,
      view.run.equipmentDrops,
    );
    return Boolean(machineView);
  } catch {
    return false;
  }
}

export function confirmRunMachine(view: RunView): boolean {
  return initializeRunMachine(view, MACHINE_CONFIRM);
}

export function rollbackRunMachine(view: RunView): boolean {
  return initializeRunMachine(view, MACHINE_ROLLBACK);
}

export function predictRunOperation(
  action: PredictionAction,
  operationSeq: number,
  acceptedAtMs: number,
  preStateHash: string,
): PredictedRun | null {
  const dispatch = window.breacherSim?.dispatch;
  if (typeof dispatch !== "function" || !machineView) return null;
  try {
    const equipmentDrops = machineView.run.equipmentDrops;
    const operation: breacher.v2.IRunOperation = {
      runId: machineView.run.id,
      opSeq: operationSeq,
      clientTickMs: acceptedAtMs,
      preStateHash: { value: hexBytes(preStateHash), revision: machineView.run.revision },
      ...encodeAction(action),
    };
    const result = decodeMachineResult(
      dispatch(
        breacher.v2.RunMachineRequest.encode({
          command: MACHINE_APPLY,
          operation,
          acceptedAtMs,
        }).finish(),
      ),
    );
    if (result.error || result.randomWindowExhausted || !result.snapshot || !result.ack)
      return null;
    const next = decodeSnapshot(
      result.snapshot,
      acceptedAtMs,
      machineView.refillPreview,
      equipmentDrops,
    );
    if (!next) return null;
    machineView = next;
    return {
      view: next,
      stateHash: bytesHex(result.ack.stateHash ?? new Uint8Array()),
      revision: longNumber(result.ack.revision),
      operationSeq: longNumber(result.ack.opSeq),
      needsAuthorityEvent: result.needsAuthorityEvent,
    };
  } catch {
    return null;
  }
}

export function advanceRunMachine(toMs: number): PredictedRun | null {
  const dispatch = window.breacherSim?.dispatch;
  if (typeof dispatch !== "function" || !machineView) return null;
  try {
    const equipmentDrops = machineView.run.equipmentDrops;
    const result = decodeMachineResult(
      dispatch(
        breacher.v2.RunMachineRequest.encode({
          command: MACHINE_ADVANCE,
          advanceToMs: toMs,
        }).finish(),
      ),
    );
    if (result.error || !result.snapshot || !result.ack) return null;
    const next = decodeSnapshot(result.snapshot, toMs, machineView.refillPreview, equipmentDrops);
    if (!next) return null;
    machineView = next;
    return {
      view: next,
      stateHash: bytesHex(result.ack.stateHash ?? new Uint8Array()),
      revision: longNumber(result.ack.revision),
      operationSeq: longNumber(result.ack.opSeq),
      needsAuthorityEvent: result.needsAuthorityEvent,
    };
  } catch {
    return null;
  }
}

export function releaseRunMachine(): void {
  const dispatch = window.breacherSim?.dispatch;
  machineView = null;
  if (typeof dispatch !== "function") return;
  try {
    dispatch(breacher.v2.RunMachineRequest.encode({ command: MACHINE_RELEASE }).finish());
  } catch {
    // The machine is already unusable; local release is complete.
  }
}

export function canPredictRun(): boolean {
  return typeof window.breacherSim?.dispatch === "function" && machineView !== null;
}

export function wouldSlideMatch(
  board: number[],
  axis: "row" | "col",
  index: number,
  distance: number,
  rules: { wrapMatches: boolean; woodOreUnion: boolean },
): boolean | null {
  const preview = window.breacherSim?.preview;
  if (typeof preview !== "function") return null;
  try {
    const request = breacher.v2.MatchPreviewRequest.encode({
      board,
      slide: { axis, index, distance },
      wrapMatches: rules.wrapMatches,
      woodOreUnion: rules.woodOreUnion,
    }).finish();
    const result = breacher.v2.MatchPreviewResult.decode(preview(request));
    return result.error ? null : result.matches;
  } catch {
    return null;
  }
}

export function findAutomaticSlide(
  board: number[],
  rules: { wrapMatches: boolean; woodOreUnion: boolean },
  startCursor = 0,
): AutomaticSlide | null {
  const automate = window.breacherSim?.automate;
  if (typeof automate !== "function") return null;
  try {
    const request = breacher.v2.AutomationRequest.encode({
      board,
      wrapMatches: rules.wrapMatches,
      woodOreUnion: rules.woodOreUnion,
      startCursor,
      policyVersion: 1,
    }).finish();
    const decision = breacher.v2.AutomationDecision.decode(automate(request));
    if (
      decision.error ||
      decision.kind !== "slide" ||
      (decision.axis !== "row" && decision.axis !== "col")
    )
      return null;
    return {
      axis: decision.axis,
      index: decision.index,
      distance: decision.distance,
      nextCursor: decision.nextCursor,
    };
  } catch {
    return null;
  }
}

async function load(): Promise<SimWasmStatus> {
  if (typeof WebAssembly === "undefined")
    return { status: "unavailable", reason: "wasm-unsupported" };
  try {
    const response = await fetch("/wasm/breacher.wasm", { cache: "no-store" });
    if (!response.ok) return { status: "unavailable", reason: `http-${response.status}` };
    await loadScript("/wasm/wasm_exec.js");
    if (!window.Go) return { status: "unavailable", reason: "go-runtime-missing" };
    const go = new window.Go();
    const bytes = await response.arrayBuffer();
    const result = await WebAssembly.instantiate(bytes, go.importObject);
    void go.run(result.instance);
    await waitForAPI();
    if (
      !window.breacherSim?.dispatch ||
      !window.breacherSim.preview ||
      !window.breacherSim.automate
    )
      return { status: "unavailable", reason: "api-missing" };
    if (EXPECTED_VERSION && window.breacherSim.version !== EXPECTED_VERSION)
      return { status: "version-mismatch" };
    return { status: "ready" };
  } catch (error) {
    return {
      status: "unavailable",
      reason: error instanceof Error ? error.message : "load-failed",
    };
  }
}

function encodeSnapshot(view: RunView): breacher.v2.IRunSnapshot {
  return {
    snapshotVersion: view.run.snapshotVersion ?? 18,
    simulationProfileId: view.run.simulationProfileId,
    engineVersion: view.run.engineVersion,
    state: encodeWireValue(compactRunMachineState(view.run)),
    randomWindow: view.randomWindow
      ? {
          consumed: view.randomWindow.consumed,
          offset: view.randomWindow.offset,
          prefetchAfter: view.randomWindow.prefetchAfter,
          chunks: view.randomWindow.chunks.map((chunk) => ({
            index: chunk.index,
            values: chunk.values,
          })),
        }
      : undefined,
    stateHash: hexBytes(view.run.syncHash || view.run.boardHash),
    serverNowMs: view.serverNow,
    machineConfig: encodeWireValue(view.machineConfig),
  };
}

function decodeSnapshot(
  snapshot: breacher.v2.IRunSnapshot | null | undefined,
  serverNow: number,
  refillPreview?: number[],
  equipmentDrops?: RunState["equipmentDrops"],
): RunView | null {
  if (!snapshot?.state || !snapshot.randomWindow || !snapshot.machineConfig) return null;
  const run = decodeWireValue(snapshot.state) as RunState;
  run.syncHash = bytesHex(snapshot.stateHash ?? new Uint8Array());
  run.boardHash = run.syncHash;
  if (equipmentDrops) run.equipmentDrops = equipmentDrops;
  return {
    run,
    serverNow,
    refillPreview: normalizeRefillPreview(refillPreview),
    randomWindow: {
      consumed: longNumber(snapshot.randomWindow.consumed),
      offset: snapshot.randomWindow.offset ?? 0,
      prefetchAfter: snapshot.randomWindow.prefetchAfter ?? 24,
      chunks: (snapshot.randomWindow.chunks ?? []).map((chunk) => ({
        index: longNumber(chunk.index),
        values: [...(chunk.values ?? [])],
      })),
    },
    machineConfig: decodeWireValue(snapshot.machineConfig) as RunView["machineConfig"],
  };
}

function encodeAction(action: PredictionAction): Partial<breacher.v2.IRunOperation> {
  switch (action.kind) {
    case "slide":
      return {
        slide: {
          axis: action.axis,
          index: action.index,
          distance: action.distance,
          automated: action.automated,
        },
      };
    case "lock_row":
      return { lockRow: { row: action.row } };
    case "skill":
      return { skill: { skill: action.skill } };
    case "chest":
      return { chest: { open: action.open } };
    case "retreat":
      return { retreat: {} };
  }
}

function decodeMachineResult(payload: Uint8Array): breacher.v2.RunMachineResult {
  return breacher.v2.RunMachineResult.decode(payload);
}

function encodeWireValue(value: unknown): breacher.v2.IWireValue {
  if (value === null || value === undefined) return { nullValue: {} };
  if (typeof value === "boolean") return { boolValue: value };
  if (typeof value === "string") return { stringValue: value };
  if (typeof value === "number") {
    if (!Number.isFinite(value)) throw new Error("Non-finite machine value");
    return Number.isSafeInteger(value) ? { intValue: value } : { doubleValue: value };
  }
  if (value instanceof Uint8Array) return { bytesValue: value };
  if (Array.isArray(value)) return { listValue: { values: value.map(encodeWireValue) } };
  if (typeof value === "object") {
    const fields: Record<string, breacher.v2.IWireValue> = {};
    for (const [key, item] of Object.entries(value))
      if (item !== undefined) fields[key] = encodeWireValue(item);
    return { objectValue: { fields } };
  }
  throw new Error("Unsupported machine value");
}

function decodeWireValue(value: breacher.v2.IWireValue | null | undefined): unknown {
  if (!value) return undefined;
  const kind = (value as breacher.v2.WireValue).kind;
  if (kind === "nullValue") return null;
  if (kind === "intValue") return longNumber(value.intValue);
  if (kind === "doubleValue") return value.doubleValue;
  if (kind === "stringValue") return value.stringValue;
  if (kind === "boolValue") return value.boolValue;
  if (kind === "bytesValue") return value.bytesValue;
  if (kind === "listValue") return value.listValue?.values?.map(decodeWireValue) ?? [];
  if (kind === "objectValue") {
    const result: Record<string, unknown> = {};
    for (const [key, item] of Object.entries(value.objectValue?.fields ?? {}))
      result[key] = decodeWireValue(item);
    return result;
  }
  return undefined;
}

function loadScript(src: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const script = document.createElement("script");
    script.src = src;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error("go-runtime-load-failed"));
    document.head.appendChild(script);
  });
}

async function waitForAPI(): Promise<void> {
  for (let attempt = 0; attempt < 20; attempt++) {
    if (window.breacherSim) return;
    await new Promise((resolve) => window.setTimeout(resolve, 10));
  }
}

function longNumber(value: number | { toNumber?: () => number } | null | undefined): number {
  if (typeof value === "number") return value;
  return value?.toNumber?.() ?? 0;
}

function hexBytes(value: string): Uint8Array {
  if (!/^[0-9a-f]{64}$/i.test(value)) return new Uint8Array();
  return Uint8Array.from({ length: value.length / 2 }, (_, index) =>
    Number.parseInt(value.slice(index * 2, index * 2 + 2), 16),
  );
}

function bytesHex(value: Uint8Array): string {
  return Array.from(value, (byte) => byte.toString(16).padStart(2, "0")).join("");
}
